import React, { ComponentProps, useState } from 'react';
import { AppContent } from '@common/components/app-content';
import { BaseButton, ButtonGroup, Gutter } from '@common/components';
import { Affix, Steps } from 'antd';
import { Colors } from '@common/tokens';
import { clsx } from 'clsx';
import { useNavigate } from '@common/hooks';

type BaseButtonProps = ComponentProps<typeof BaseButton> & {
  confirmButtonText?: string;
};

function GoBackButton(props: BaseButtonProps) {
  return (
    <BaseButton size="large" data-testid="go-back-button" {...props}>
      Back
    </BaseButton>
  );
}

function ForwardButton(props: BaseButtonProps) {
  return (
    <BaseButton
      type="primary"
      size="large"
      data-testid="continue-button"
      {...props}
    >
      Continue
    </BaseButton>
  );
}

function ConfirmButton({ confirmButtonText, ...props }: BaseButtonProps) {
  return (
    <BaseButton
      type="primary"
      size="large"
      data-testid="confirm-button"
      {...props}
    >
      {confirmButtonText || 'Confirm'}
    </BaseButton>
  );
}

function StepTitle({
  title,
  index,
  onClick,
  hasHyperlink,
}: {
  title: string;
  index: number;
  onClick: () => void;
  hasHyperlink: boolean;
}) {
  if (hasHyperlink) {
    return (
      <>
        <BaseButton
          type="link"
          className="page-steps-header-link"
          onClick={onClick}
        >
          <span>{index + 1}.&nbsp;</span>
          {title}
        </BaseButton>
        <style jsx>{`
          :global(.page-steps-header-link) {
            font-weight: 500;
          }
          :global(.page-steps-header-link span:first-child) {
            color: ${Colors.textDefault};
          }
        `}</style>
      </>
    );
  }

  return (
    <>
      {index + 1}. {title}
    </>
  );
}

type ButtonsAlignment = 'left' | 'right';

type PageStep = {
  title: string;
  page: JSX.Element;
  validate?: () => boolean | Promise<boolean>;
  forwardButtonDisabled?: boolean;
  forwardButtonLoading?: boolean;
  headerSlot?: React.ReactNode;
  buttonsAlignment?: ButtonsAlignment;
  customForwardAction?: (forwardAction: () => void) => JSX.Element;
  confirmButtonText?: string;
};

type Props = {
  steps: PageStep[];
  onConfirm: () => void;
  confirmLoading?: boolean;
  initialStepIndex?: number;
  onCancel?: () => void;
};

export function PageSteps({
  steps,
  onConfirm,
  confirmLoading = false,
  initialStepIndex = 0,
  onCancel,
}: Props) {
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex);
  const navigate = useNavigate();

  if (!steps?.length) throw new Error("Props steps can't be empty");

  const isFirstStep = !currentStepIndex;
  const isLastStep = currentStepIndex === steps.length - 1;
  const currentStep = steps[currentStepIndex];

  async function handleForward() {
    const canGoForward = (await currentStep.validate?.()) ?? true;
    if (!canGoForward) return;

    setCurrentStepIndex((prevState: number) => prevState + 1);
  }

  function handleBackward() {
    setCurrentStepIndex((prevState: number) => prevState - 1);
  }

  const isButtonDisabled =
    currentStep.forwardButtonDisabled && !currentStep.forwardButtonLoading;

  return (
    <div className="page-steps">
      <AppContent>
        <header className="page-steps__header">
          <Affix offsetTop={65}>
            <div className="page-steps__breadcrumb">
              <Steps
                current={currentStepIndex}
                type="navigation"
                size="small"
                items={steps.map((step, index) => ({
                  title: (
                    <StepTitle
                      title={step.title}
                      index={index}
                      hasHyperlink={index < currentStepIndex}
                      onClick={() => setCurrentStepIndex(index)}
                    />
                  ),
                  icon: <></>,
                }))}
              />
            </div>
          </Affix>
          {currentStep.headerSlot && (
            <Gutter
              size="md"
              direction="vertical"
              className="page-steps__header-slot"
            >
              {currentStep.headerSlot}
            </Gutter>
          )}
        </header>
        <main className="page-steps__main-content">{currentStep.page}</main>
        <footer
          className={clsx('page-steps__footer', {
            'page-steps__footer--align-left':
              currentStep.buttonsAlignment === 'left',
          })}
        >
          <ButtonGroup>
            {isFirstStep ? (
              <BaseButton
                size="large"
                data-testid="cancel-button"
                onClick={() => (onCancel ? onCancel() : navigate(-1))}
              >
                Cancel
              </BaseButton>
            ) : (
              <GoBackButton onClick={handleBackward} />
            )}

            {currentStep.customForwardAction ? (
              currentStep.customForwardAction(
                isLastStep ? onConfirm : handleForward
              )
            ) : isLastStep ? (
              <ConfirmButton
                confirmButtonText={currentStep.confirmButtonText}
                loading={confirmLoading}
                disabled={isButtonDisabled}
                onClick={onConfirm}
              />
            ) : (
              <ForwardButton
                disabled={isButtonDisabled}
                loading={currentStep.forwardButtonLoading}
                onClick={handleForward}
              />
            )}
          </ButtonGroup>
        </footer>
      </AppContent>
      <style jsx>{`
        .page-steps__header {
          position: relative;
        }
        .page-steps__breadcrumb {
          border-bottom: 1px solid ${Colors.steel};
          background-color: ${Colors.white};
        }
        .page-steps__main-content {
          padding: 32px 0;
        }
        .page-steps__footer {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          background-color: ${Colors.white};
        }
        .page-steps__footer--align-left {
          justify-content: flex-start;
        }
        .page-steps__control-buttons :global(.ant-btn) {
          margin-left: 15px;
        }
        .page-steps__footer :global(.ant-modal-footer) {
          text-align: right;
        }
        .page-steps :global(.ant-steps-item-title) {
          font-size: 18px;
          font-weight: 500;
          line-height: 32px;
        }
        .page-steps :global(.page-steps-header-link) {
          font-size: 18px;
          padding: 0;
          margin: 0;
          border: none;
        }
        .page-steps :global(.ant-steps) {
          display: block;
        }
        .page-steps :global(.ant-steps-navigation .ant-steps-item::after) {
          margin-left: 10px;
        }
        .page-steps
          :global(
            .ant-steps-small.ant-steps-horizontal:not(.ant-steps-label-vertical)
              .ant-steps-item
          ) {
          margin-right: 45px;
        }
        .page-steps :global(.ant-steps-navigation) {
          padding-top: 20px;
        }
        .page-steps :global(.ant-steps-navigation .ant-steps-item-container) {
          padding-bottom: 20px;
        }
      `}</style>
    </div>
  );
}
